/**
 * Mark-spam and unmark-spam mutations for the agent detail page.
 *
 * The spam flow in SPEC §9 is "mark this ticket as spam AND add the
 * sender to the blocklist so future emails from this sender are
 * auto-marked." The backend defaults `add_sender_block` to true; the
 * confirmation modal in the UI is what surfaces this consequence to
 * the agent.
 *
 * Both mutations invalidate the detail (status flips to spam or back),
 * the inbox list (the row leaves the default view when status=spam),
 * and the counts (the Spam pill count moves).
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type { MarkSpamRequest, TicketResponse } from "@/types/api";

import { agentTicketCountsQueryKey } from "./useAgentTicketCounts";
import { agentTicketDetailQueryPrefix } from "./useAgentTicketDetail";

function applyResponse(
  queryClient: ReturnType<typeof useQueryClient>,
  ticketNumber: number,
  updated: TicketResponse,
): void {
  // Detail cache is keyed by lowercase URL code (e.g. "ku1"); the
  // mark-spam mutation only knows the numeric ticket_number, so we
  // match by prefix and pick the entry whose payload has the matching
  // ticket_number. Without this the spam badge would flash and then
  // revert until the user refreshed.
  queryClient.setQueriesData<TicketResponse>(
    { queryKey: agentTicketDetailQueryPrefix },
    (prev) => (prev?.ticket_number === ticketNumber ? updated : prev),
  );
  void queryClient.invalidateQueries({
    queryKey: agentTicketDetailQueryPrefix,
  });
  void queryClient.invalidateQueries({
    queryKey: ["agent", "tickets", "list"],
  });
  void queryClient.invalidateQueries({
    queryKey: agentTicketCountsQueryKey,
  });
}

export function useMarkSpam(
  ticketNumber: number,
): UseMutationResult<TicketResponse, Error, MarkSpamRequest | void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const response = await api.post<TicketResponse>(
        `/agent/tickets/${ticketNumber}/mark-spam`,
        body ?? {},
      );
      return response.data;
    },
    onSuccess: (updated) => applyResponse(queryClient, ticketNumber, updated),
  });
}

export function useUnmarkSpam(
  ticketNumber: number,
): UseMutationResult<TicketResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await api.post<TicketResponse>(
        `/agent/tickets/${ticketNumber}/unmark-spam`,
      );
      return response.data;
    },
    onSuccess: (updated) => applyResponse(queryClient, ticketNumber, updated),
  });
}
